import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../util/api";
import { useHistory } from "react-router-dom";
import BidDetailModal from "./BidDetailModal";

import {
    Box,
    Container,
    Typography,
    Stack,
    TextField,
    Button,
    Chip,
    IconButton,
    Collapse,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    CircularProgress, LinearProgress, Paper, Tooltip,
    TablePagination, Tabs, Tab,
} from "@mui/material";
import { useNotifyDialog } from "../../component/common/NotifyDialog";
import { notify } from "../../util/toast";

import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {FilterRow, FilterTable} from "../../component/FilterTable";
import UnifiedSearchBar from "./SearchBar";
import FavoriteStar from "./components/FavoriteStar";
import { favoriteApi } from "./api/favoriteApi";
import LoadingDots from "../../component/common/LoadingDots";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatDate, formatDateTimeMinute, today as getToday, toDayjs } from "../../util/dateUtils";
import { ds } from "../../styles/designSystem";
import { useMyPermissions } from "../../util/useMyPermissions";
import ActionButton from "../../component/common/ActionButton";
import ComboBox from "../../component/common/ComboBox";
import ResizableTable from "../../component/common/ResizableTable";

const today = formatDate(getToday());
// 절대 날짜는 자유 선택 (미래만 max=today 로 차단). 기간 차이 한계는 아래 MAX_*_PERIOD_DAYS 로 따로 가드.

/**
 * 입력값을 일정 지연 후의 안정된 값으로 반환하는 훅.
 * 금액 입력처럼 타이핑 중인 값에 대해 매 키스트로크 fetch 를 막기 위해 사용.
 */
function useDebounce(value, delayMs) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

export default function MainSearchPage() {
    const history = useHistory();
    const { hasPerm, isAdmin } = useMyPermissions();
    const canRefresh = isAdmin || hasPerm("BID_REFRESH");
    const [showNotice, setShowNotice] = useState(false);
    const [keywords, setKeywords] = useState([]);

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    // ── 추가 필터 ────────────────────────────────────────────
    const [dateField, setDateField] = useState("rgstDt"); // 기간 적용 필드
    const [statusFilter, setStatusFilter] = useState(""); // 빈 값=전체
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");

    // 금액 텍스트 필드는 타이핑 중 매 글자마다 fetch 를 피하기 위해 350ms debounce.
    const debouncedMinPrice = useDebounce(minPrice, 350);
    const debouncedMaxPrice = useDebounce(maxPrice, 350);

    // 첫 마운트 시 fetchUserKeyword 가 keywords 를 채워 넣기 전엔 자동 재조회를 시작하지 않음.
    // 이 플래그가 true 가 된 이후부터 필터/키워드 변경에 반응한다.
    const [keywordsLoaded, setKeywordsLoaded] = useState(false);

    const [highlighted, setHighlighted] = useState("");
    const [newKeyword, setNewKeyword] = useState("");

    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);

    // 같은 공고번호의 차수들을 묶어, 펼친(이전 차수 표시) 공고번호 key 집합.
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());

    const [sortKey, setSortKey] = useState("rgstDt");
    const [sortAsc, setSortAsc] = useState(false); // 최신순(desc) 디폴트 — 백엔드 정렬과 일치
    const [isLoading, setIsLoading] = useState(false);

    // ── 페이지네이션 ────────────────────────────────────────
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);

    // ── Tabs: 0=전체 / 1=즐겨찾는 공고 / 2=즐겨찾는 기업
    const [tab, setTab] = useState(0);

    /**
     * 개찰 추적 현황판(관리자 탭) — «결과가 아직 안 난» 공고를 상태별로 본다.
     *
     * 지금까지 «이건 언제 다시 보나», «몇 건이 실패로 멈춰 있나» 를 서버 로그를 grep 해야
     * 알 수 있었다. 판단 근거가 스케줄러와 같은 컬럼이라, 여기 보이는 상태가 곧 다음 사이클의 동작이다.
     */
    /** 상태 뱃지 색 — «멈춤» 만 눈에 띄어야 한다. 나머지는 정상 흐름이라 조용한 색. */
    const TRACK_BADGE = {
        BEFORE:   { label: "개찰 전",  bg: "#ede9fe", color: "#7c3aed" },
        TRACKING: { label: "추적 중",  bg: "#e0f2fe", color: "#0369a1" },
        FAILED:   { label: "확인 실패", bg: "#fee2e2", color: "#b91c1c" },
    };
    const TRACK_STATUS = [
        { value: "ALL",      label: "전체" },
        { value: "BEFORE",   label: "개찰 전" },
        { value: "TRACKING", label: "추적 중" },
        { value: "FAILED",   label: "확인 실패(멈춤)" },
    ];
    const [trackStatus, setTrackStatus] = useState("TRACKING");
    const [trackPage, setTrackPage] = useState(0);
    const [trackSize, setTrackSize] = useState(100);
    /** 마지막 조회 시각 — 아래 «스냅샷» 주의 문구가 가리키는 시점. */
    const [trackFetchedAt, setTrackFetchedAt] = useState(null);
    /** 새로고침 트리거 — 값이 바뀌면 조회 effect 가 다시 돈다. */
    const [trackReload, setTrackReload] = useState(0);
    const [trackData, setTrackData] = useState(null);
    const [trackLoading, setTrackLoading] = useState(false);
    /** 관리자 탭의 인덱스 — 권한이 없으면 탭 자체를 안 그리므로 존재하지 않는다. */
    const TRACK_TAB = 3;
    // 즐겨찾기 공고/기업 detail — 탭 진입 시 fetch.
    const [favBidItems, setFavBidItems] = useState([]);
    const [favCompanyItems, setFavCompanyItems] = useState([]);
    const [favTabLoading, setFavTabLoading] = useState(false);
    const reloadFavBidItems = async () => {
        setFavTabLoading(true);
        try { setFavBidItems(await favoriteApi.myBidItems() || []); }
        catch { setFavBidItems([]); }
        finally { setFavTabLoading(false); }
    };
    const reloadFavCompanyItems = async () => {
        setFavTabLoading(true);
        try { setFavCompanyItems(await favoriteApi.myCompanyItems() || []); }
        catch { setFavCompanyItems([]); }
        finally { setFavTabLoading(false); }
    };
    useEffect(() => {
        if (tab === 1) reloadFavBidItems();
        else if (tab === 2) reloadFavCompanyItems();
        // eslint-disable-next-line
    }, [tab]);

    // ── 즐겨찾기 — 현재 페이지에 표시된 공고 중 내가 즐겨찾기한 id Set.
    //    results 가 갱신될 때마다 서버에 filter 요청 → 별 토글 상태를 한 번에 받음.
    const [bidFavSet, setBidFavSet] = useState(() => new Set());
    const toggleBidFav = async (bidId, nextChecked) => {
        // 낙관적 UI — 별을 먼저 즉시 갱신, API 는 background.
        setBidFavSet(prev => {
            const next = new Set(prev);
            if (nextChecked) next.add(bidId); else next.delete(bidId);
            return next;
        });
        try {
            if (nextChecked) await favoriteApi.addBid(bidId);
            else             await favoriteApi.removeBid(bidId);
        } catch (e) {
            // 실패 시 롤백
            setBidFavSet(prev => {
                const next = new Set(prev);
                if (nextChecked) next.delete(bidId); else next.add(bidId);
                return next;
            });
            notify(`즐겨찾기 처리 실패 — ${e?.message || "네트워크 오류"}`, "error");
        }
    };


    // ── 동시성 가드 — 가장 마지막 요청만 결과 반영 ────────────
    const fetchAbortRef = useRef(null);
    const fetchSeqRef = useRef(0);

    const saveKeywordsToServer = async (keywordList) => {
        try {
            await api.post(`${process.env.REACT_APP_API_BASE_URL}/employees/me/keywords`, {
                keywords: keywordList.join(","),
            });
        } catch {}
    };

    const fetchUserKeyword = async () => {
        try {
            const data = await api.get(`${process.env.REACT_APP_API_BASE_URL}/employees/me/keywords`);
            setKeywords(Array.isArray(data) ? data : []);
        } catch {}
    };

    /**
     * 기간 한도 — 서버가 정한다. «조회» 와 «갱신» 둘 다.
     *
     * 일반 사용자는 조회 60일 / 갱신 30일. «나라장터 공고 관리자» 계정(서버 설정
     * g2b.refresh.admin-accounts)은 <b>셋 다 상한 없음</b> — 조회·공고 갱신·낙찰 갱신.
     * 프런트가 계정 이름을 직접 알고 판단하면 규칙이 두 곳으로 갈라지므로 결과만 받아 쓴다.
     */
    const [refreshScope, setRefreshScope] = useState({ unlimited: false, maxDays: 30, searchMaxDays: 60, openCycleCap: 2000 });
    useEffect(() => {
        if (!canRefresh) return;
        let cancelled = false;
        api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/refresh-scope`)
            .then((r) => { if (!cancelled && r) setRefreshScope(r); })
            .catch((e) => {
                // 실패 시 기본값(조회 60일·갱신 30일) 유지 — 더 넓게 열지 않는다.
                // 다만 통째로 삼키면 «관리자인데 왜 30일에서 막히지» 를 추적할 단서가 없다.
                console.warn("[나라장터] 기간 한도 조회 실패 — 기본 한도로 동작합니다", e);
            });
        return () => { cancelled = true; };
    }, [canRefresh]);

    /**
     * 조회(검색) 기간 차이 상한 — 기본 60일(=2달), 관리자 계정은 상한 없음.
     * 백엔드 MAX_SEARCH_PERIOD_DAYS 와 «관리자 예외까지» 같은 규칙이다.
     */
    useEffect(() => {
        if (tab !== TRACK_TAB || !canRefresh) return;
        let cancelled = false;
        setTrackLoading(true);
        const q = new URLSearchParams({ status: trackStatus, page: String(trackPage), size: String(trackSize) });
        api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/open-tracking?${q.toString()}`)
            .then((d) => { if (!cancelled) { setTrackData(d); setTrackFetchedAt(new Date()); } })
            .catch((e) => {
                if (!cancelled) setTrackData(null);
                // 조용히 비우면 «건이 없는 것» 과 «못 불러온 것» 이 화면에서 같아 보인다.
                console.warn("[나라장터] 개찰 추적 현황판 조회 실패", e);
            })
            .finally(() => { if (!cancelled) setTrackLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, canRefresh, trackStatus, trackPage, trackSize, trackReload]);

    const MAX_PERIOD_DAYS = refreshScope.unlimited
        ? Number.POSITIVE_INFINITY
        : (refreshScope.searchMaxDays ?? 60);
    /** 화면 표기용 — Infinity 가 그대로 찍히면 «최대 Infinity일» 이 된다. */
    const searchLimitText = refreshScope.unlimited ? "기간 제한 없음" : `최대 ${MAX_PERIOD_DAYS}일`;
    /**
     * 갱신 기간 차이 최대 30일 (=한 달) — 외부 API 부하 회피.
     * 일자 분할(4h×6 청크 병렬) 처리라 조달청 1개월 한계와 무관하지만, 사용자 편의/시스템 부하 회피용.
     */
    const MAX_REFRESH_PERIOD_DAYS = refreshScope.unlimited
        ? Number.POSITIVE_INFINITY
        : (refreshScope.maxDays ?? 30);
    /** 화면 표기용 — Infinity 가 그대로 찍히면 «최대 Infinity일» 이 된다. */
    const refreshLimitText = refreshScope.unlimited ? "기간 제한 없음" : `최대 ${MAX_REFRESH_PERIOD_DAYS}일`;
    /**
     * 날짜 피커가 허용할 폭 = 조회 상한.
     *
     * 예전엔 «조회는 60일 고정, 갱신만 무제한» 이라 피커 폭을 따로 뒀다. 관리자 계정은 조회 상한도
     * 풀렸으므로 둘이 같아졌지만 상수는 남겨 둔다 — 두 값이 다시 갈릴 때 고칠 곳을 한 군데로 두려고.
     */
    const PICKER_MAX_DAYS = MAX_PERIOD_DAYS;

    /**
     * 시작일 변경 — 차이가 PICKER_MAX_DAYS 를 넘으면 종료일을 자동으로 좁혀 cap.
     * 절대 날짜 선택은 자유지만 차이는 상한 이내로 강제(관리자 계정은 상한이 없어 cap 이 걸리지 않는다).
     */
    const handleStartDateChange = (raw) => {
        setStartDate(raw);
        if (!raw || !endDate) return;
        const s = toDayjs(raw);
        const e = toDayjs(endDate);
        if (e.diff(s, "day") > PICKER_MAX_DAYS) {
            setEndDate(formatDate(s.add(PICKER_MAX_DAYS, "day")));
        }
        if (e.isBefore(s)) {
            // 시작이 종료보다 늦어진 경우 종료를 시작과 동일하게.
            setEndDate(raw);
        }
    };

    /** 종료일 변경 — 차이가 PICKER_MAX_DAYS 를 넘으면 시작일을 자동으로 좁혀 cap. */
    const handleEndDateChange = (raw) => {
        setEndDate(raw);
        if (!raw || !startDate) return;
        const s = toDayjs(startDate);
        const e = toDayjs(raw);
        if (e.diff(s, "day") > PICKER_MAX_DAYS) {
            setStartDate(formatDate(e.subtract(PICKER_MAX_DAYS, "day")));
        }
        if (e.isBefore(s)) {
            setStartDate(raw);
        }
    };

    /**
     * 기간 검증 — UI 가드. 백엔드도 동일 검증하지만 사용자에게 빠른 피드백 제공.
     * @param {boolean} silent true 면 alert 없이 boolean 만 반환 (자동 재조회 effect 용)
     * @returns {boolean} 유효하면 true
     */
    const validatePeriod = (silent = false) => {
        const start = toDayjs(startDate);
        const end = toDayjs(endDate);
        if (start.isAfter(end)) {
            if (!silent) notify("시작일이 종료일보다 늦을 수 없습니다.", "warning");
            return false;
        }
        const days = end.diff(start, "day");
        if (days > MAX_PERIOD_DAYS) {
            if (!silent) notify(`검색 기간은 ${searchLimitText}까지 가능합니다. 현재 ${days}일 — 기간을 좁혀주세요.`, "warning");
            return false;
        }
        return true;
    };

    /**
     * 페이지 데이터 조회. 다른 인자 변경 시 page:0 으로 리셋해야 함.
     * 동시 요청 race condition 가드 — 마지막 요청만 반영, 이전 in-flight 는 무시.
     */
    const fetchData = async (overrides = {}) => {
        // 자동 재조회 effect 에서 호출 시 alert 띄우지 않음.
        if (!validatePeriod(overrides.silent === true)) return;

        const reqPage = overrides.page ?? page;
        const reqSize = overrides.size ?? pageSize;
        const reqSortBy = overrides.sortBy ?? sortKey;
        const reqSortDir = overrides.sortDir ?? (sortAsc ? "asc" : "desc");

        // 이전 in-flight 요청 취소
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        const controller = new AbortController();
        fetchAbortRef.current = controller;
        const seq = ++fetchSeqRef.current;

        setIsLoading(true);
        setResults([]);
        try {
            const query = new URLSearchParams();
            (keywords || []).forEach((k) => query.append("keywords", k));
            query.append("startDate", startDate);
            query.append("endDate", endDate);
            query.append("dateField", dateField);
            if (statusFilter) query.append("status", statusFilter);
            if (minPrice !== "" && !isNaN(Number(minPrice))) query.append("minPrice", String(Number(minPrice)));
            if (maxPrice !== "" && !isNaN(Number(maxPrice))) query.append("maxPrice", String(Number(maxPrice)));
            query.append("page", String(reqPage));
            query.append("size", String(reqSize));
            query.append("sortBy", reqSortBy);
            query.append("sortDir", reqSortDir);
            const data = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/bids/list?${query.toString()}`,
                undefined,
                { signal: controller.signal }
            );

            // 최신 요청이 아니면 결과 무시 (race condition 방지)
            if (seq !== fetchSeqRef.current) return;

            const content = Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : []);
            setResults(content);
            setTotalElements(typeof data?.totalElements === "number" ? data.totalElements : content.length);
        } catch (err) {
            // axios 의 cancel 은 환경/버전에 따라 name/code 가 다양 — 모두 정상 종료로 간주.
            if (err?.name === "AbortError"
                || err?.name === "CanceledError"
                || err?.code === "ERR_CANCELED"
                || err?.code === "ERR_NETWORK" && controller.signal.aborted
                || err?.message === "canceled") {
                return;
            }
            // 사용자가 직접 abort 한 경우(필터 변경 등) — silent
            if (controller.signal.aborted) return;
            console.error("검색 실패", err);
            const msg = err?.response?.data?.message || err?.message || "검색 실패";
            notify(`검색 실패 — ${msg}`, "error");
        } finally {
            if (seq === fetchSeqRef.current) setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(0);
        fetchData({ page: 0 });
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
        fetchData({ page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        setPage(0);
        fetchData({ page: 0, size: newSize });
    };

    /**
     * 낙찰 갱신 — SSE 진행률 스트리밍.
     *
     * <p>백엔드 {@code POST /api/bids/refresh-winning-by-period/stream} 를 호출.
     * fetch + ReadableStream 으로 SSE 라인 단위 파싱.
     *
     * <p>SSE 이벤트:
     *  - started  : { totalTargets, startDate, endDate }
     *  - progress : { processed, total, succeeded, failed, currentBidNo, percent }
     *  - done     : { totalTargets, succeeded, failed, elapsedMs }
     *  - error    : { code, resultCode, message, rawMessage, processed, succeeded, failed }
     */
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(null);
    const [refreshError, setRefreshError] = useState(null);
    /** SSE 단계별 메시지 누적 — 프로그레스바 아래에 실시간 표시용. */
    const [refreshLog, setRefreshLog] = useState([]);
    const appendRefreshLog = (msg) => setRefreshLog((prev) => [
        ...prev,
        { at: new Date(), text: msg },
    ]);
    /** 갱신 시작 시각 (ms) — ETA 계산 기준. */
    const [refreshStartedAt, setRefreshStartedAt] = useState(null);
    /** 로그 박스 자동 스크롤 — 새 메시지마다 맨 아래로. */
    const refreshLogRef = useRef(null);
    useEffect(() => {
        if (refreshLogRef.current) {
            refreshLogRef.current.scrollTop = refreshLogRef.current.scrollHeight;
        }
    }, [refreshLog]);

    /** 진행 중인 SSE 요청들의 AbortController — 페이지 이탈/언마운트 시 일괄 abort 용. */
    const refreshAbortRef = useRef(null);
    const annAbortRef = useRef(null);

    /**
     * 갱신 호출 전 검증 — 갱신은 자기 상한(MAX_REFRESH_PERIOD_DAYS)만 본다.
     *
     * ★ 여기서 validatePeriod() 를 먼저 태우면 안 된다. 그건 «조회» 상한(60일)이라,
     *   갱신 상한이 풀린 관리자 계정도 61일째부터 «검색 기간은 최대 60일» 로 막혔다.
     *   두 상한은 성격이 다르다 — 조회는 화면 부하, 갱신은 조달청 호출량이다.
     *   시작>종료 검사만 갱신 쪽에서도 필요하므로 그것만 여기서 다시 한다.
     */
    const validateRefreshPeriod = () => {
        const start = toDayjs(startDate);
        const end = toDayjs(endDate);
        if (start.isAfter(end)) {
            notify("시작일이 종료일보다 늦을 수 없습니다.", "warning");
            return false;
        }
        const days = end.diff(start, "day");
        if (days > MAX_REFRESH_PERIOD_DAYS) {
            notify(`갱신은 ${refreshLimitText}까지 가능합니다. 현재 ${days}일 — 기간을 좁혀주세요.`, "warning");
            return false;
        }
        return true;
    };

    /**
     * 낙찰 결과 갱신 — 기간이거나, 콕 집은 공고 목록이거나.
     *
     * @param ids 지정 목록. 없으면 화면의 조회기간을 쓴다.
     *            현황판의 «N건 갱신» 이 이 경로로 온다 — 화면에 이미 대상이 떠 있는데
     *            그걸 날짜로 환산해 부르면 화면에 없는 건까지 딸려 온다.
     */
    /**
     * 한 번의 SSE 요청 = 한 청크.
     *
     * @param chunkBase  이 청크 앞에서 이미 끝낸 건수 — 진행률을 전체 기준으로 환산한다
     * @param grandTotal 전체 요청 건수. 0 이면 서버가 알려 준 값을 그대로 쓴다(기간 갱신)
     */
    const runWinningStream = async (ids, chunkBase = 0, grandTotal = 0) => {
        const byIds = Array.isArray(ids) && ids.length > 0;

        const ctrl = new AbortController();
        refreshAbortRef.current = ctrl;
        const token = localStorage.getItem("auth_token");
        const query = new URLSearchParams();
        query.append("startDate", startDate);
        query.append("endDate", endDate);
        const url = byIds
            ? `${process.env.REACT_APP_API_BASE_URL}/bids/refresh-winning-by-ids/stream`
            : `${process.env.REACT_APP_API_BASE_URL}/bids/refresh-winning-by-period/stream?${query.toString()}`;

        try {
            const response = await fetch(url, {
                method: "POST",
                signal: ctrl.signal,
                headers: {
                    "Accept": "text/event-stream",
                    ...(byIds ? { "Content-Type": "application/json" } : {}),
                    ...(token ? { "X-TOKEN": token } : {}),
                },
                ...(byIds ? { body: JSON.stringify(ids) } : {}),
            });

            if (!response.ok) {
                let msg = "낙찰 갱신 요청 실패";
                try {
                    const json = await response.json();
                    msg = json?.message || msg;
                } catch (_) {}
                throw new Error(msg);
            }

            // SSE 라인 단위 파싱 — 챗봇과 동일 패턴
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            let currentEvent = "message";
            let finalDone = null;
            let finalError = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let idx;
                while ((idx = buffer.indexOf("\n")) >= 0) {
                    const line = buffer.slice(0, idx).replace(/\r$/, "");
                    buffer = buffer.slice(idx + 1);

                    if (line === "") { currentEvent = "message"; continue; }
                    if (line.startsWith("event:")) {
                        currentEvent = line.slice(6).trim();
                    } else if (line.startsWith("data:")) {
                        const data = line.slice(5).trim();
                        try {
                            const parsed = JSON.parse(data);
                            if (currentEvent === "started") {
                                setRefreshProgress((p) => ({ ...p, phase: "running",
                                    total: grandTotal || (parsed.totalTargets ?? 0), percent: 0,
                                    processed: 0, succeeded: 0, failed: 0,
                                }));
                                appendRefreshLog(
                                    `대상 ${parsed.totalTargets ?? 0}건 — 갱신 시작` +
                                    // 개찰 전 건은 «대상 아님» 이지 «누락» 이 아니다 — 숫자로 말해 준다.
                                    (parsed.beforeOpening ? ` (개찰전 ${parsed.beforeOpening}건은 제외)` : "") +
                                    // 목록 지정인데 요청보다 대상이 적으면 그 사이 처리된 것이다.
                                    (parsed.requested && parsed.requested !== parsed.totalTargets
                                        ? ` (요청 ${parsed.requested}건 중 이미 처리된 ${parsed.requested - parsed.totalTargets}건 제외)`
                                        : "")
                                );
                            } else if (currentEvent === "progress") {
                                // 서버가 주는 processed·percent 는 «이 청크 안» 기준이다.
                                // 앞 묶음에서 끝낸 만큼을 더해 전체 기준으로 환산한다.
                                const doneAll = chunkBase + (parsed.processed ?? 0);
                                const totalAll = grandTotal || (parsed.total ?? 0);
                                setRefreshProgress({
                                    phase: "running",
                                    processed: doneAll,
                                    total: parsed.total,
                                    succeeded: parsed.succeeded,
                                    failed: parsed.failed,
                                    currentBidNo: parsed.currentBidNo,
                                    currentBidName: parsed.currentBidName,
                                    winningCompanyName: parsed.winningCompanyName,
                                    total: totalAll,
                                    percent: totalAll > 0 ? Math.round((doneAll / totalAll) * 100) : (parsed.percent ?? 0),
                                });
                                const shortName = parsed.currentBidName
                                    ? (parsed.currentBidName.length > 28
                                        ? parsed.currentBidName.slice(0, 28) + "…"
                                        : parsed.currentBidName)
                                    : "";
                                const winnerStr = parsed.winningCompanyName
                                    ? ` → 낙찰: ${parsed.winningCompanyName}`
                                    : "";
                                appendRefreshLog(
                                    `[${parsed.processed}/${parsed.total}] ${parsed.currentBidNo}${shortName ? " · " + shortName : ""}${winnerStr}`
                                );
                            } else if (currentEvent === "done") {
                                finalDone = parsed;
                                appendRefreshLog(`완료 — 성공 ${parsed.succeeded ?? 0} · 실패 ${parsed.failed ?? 0}`);
                            } else if (currentEvent === "error") {
                                finalError = parsed;
                                appendRefreshLog(`❌ 오류: ${parsed.message || parsed.code}`);
                            }
                        } catch (_) {}
                    }
                }
            }

            if (finalError) {
                setRefreshError(finalError);
                // 여러 줄 결과 요약 — 기본 2.5초로는 못 읽으므로 길게 띄운다.
                notify(
                    `낙찰 갱신 중단\n` +
                    `사유: ${finalError.message}\n` +
                    `(코드: ${finalError.resultCode ?? finalError.code})\n` +
                    `처리: ${finalError.processed ?? 0}건 (성공 ${finalError.succeeded ?? 0} · 실패 ${finalError.failed ?? 0})`,
                    "error",
                    { autoClose: 8000 },
                );
            } else if (finalDone) {
                notify(
                    `낙찰 갱신 완료\n` +
                    `대상: ${finalDone.totalTargets}건\n` +
                    // «성공 500건» 만으로는 새로 채운 건지 확인만 한 건지 알 수 없다.
                    (finalDone.beforeOpening ? `개찰전: ${finalDone.beforeOpening}건 (아직 때가 아님 — 대상 제외)\n` : "") +
                    `갱신: ${finalDone.updated ?? 0}건 (값이 달라진 건)\n` +
                    `유지: ${finalDone.unchanged ?? 0}건 (다시 봤는데 같음)\n` +
                    `미게시: ${finalDone.pending ?? 0}건 (조달청이 아직 안 올림 — 실패 아님)\n` +
                    `실패: ${finalDone.failed}건` +
                    (finalDone.elapsedMs ? `\n소요: ${(finalDone.elapsedMs / 1000).toFixed(1)}초` : ""),
                    "success",
                    { autoClose: 8000 },
                );
                await fetchData();
            }
        } catch (err) {
            if (err.name === "AbortError") {
                appendRefreshLog("⚠️ 사용자 이탈/취소 — 작업 중단됨");
                return;
            }
            console.error("낙찰 갱신 실패", err);
            setRefreshError({ code: "NETWORK_ERROR", message: err.message });
            appendRefreshLog(`❌ 네트워크 오류: ${err.message || ""}`);
            notify(`낙찰 갱신 실패 — ${err.message || "네트워크 오류"}`, "error");
        } finally {
            setIsRefreshing(false);
            refreshAbortRef.current = null;
            // 진행률 패널은 결과 확인 가능하도록 4초 동안 유지 후 자동 닫기
            setTimeout(() => setRefreshProgress(null), 4000);
        }
    };

    /**
     * 한 요청에 실을 최대 건수.
     *
     * <p>5000건을 한 번에 보냈다가 SSE 가 정확히 상한(2,000초)에서 잘렸다 — 서버 타임아웃이
     * «마지막 이벤트 이후» 가 아니라 <b>전체 경과</b> 기준임이 그때 확인됐다. 한 요청을 길게
     * 잡을수록 잘릴 확률만 커지므로, 길게 만들지 말고 <b>여러 번 나눠</b> 보낸다.
     * 끊겨도 앞 청크는 이미 저장돼 있고, 다시 눌러도 처리된 건은 대상에서 빠진다.
     */
    const REFRESH_CHUNK = 500;

    /**
     * 낙찰 결과 갱신 — 기간이거나, 콕 집은 공고 목록이거나.
     *
     * @param ids 지정 목록. 없으면 화면의 조회기간을 쓴다.
     */
    /**
     * @param preConfirmed 호출자가 이미 확인을 받았으면 true — 확인창을 두 번 띄우지 않는다.
     *                     («필터 전체 갱신» 은 예상 시간까지 담은 자기 확인창을 먼저 띄운다)
     */
    const refreshWinning = async (ids = null, preConfirmed = false) => {
        const byIds = Array.isArray(ids) && ids.length > 0;
        if (!byIds && !validateRefreshPeriod()) return;

        const chunks = byIds
            ? Array.from({ length: Math.ceil(ids.length / REFRESH_CHUNK) },
                         (_, i) => ids.slice(i * REFRESH_CHUNK, (i + 1) * REFRESH_CHUNK))
            : [null];

        const ok = preConfirmed || await confirmAsync({
            title: "낙찰 결과 갱신",
            message: byIds
                ? `지금 목록에 보이는 ${ids.length}건의 낙찰 결과를 갱신합니다.`
                  + (chunks.length > 1 ? ` ${REFRESH_CHUNK}건씩 ${chunks.length}번에 나눠 돌립니다.` : "")
                  + " 이미 처리된 건은 건너뜁니다. 계속할까요?"
                : `${startDate} ~ ${endDate} 기간 미갱신 공고의 낙찰 결과를 갱신합니다. 시간이 걸릴 수 있습니다. 계속할까요?`,
        });
        if (!ok) return;

        setIsRefreshing(true);
        setRefreshError(null);
        setRefreshLog([]);
        setRefreshStartedAt(Date.now());
        setRefreshProgress({ phase: "starting", processed: 0, total: byIds ? ids.length : 0,
                             succeeded: 0, failed: 0, percent: 0 });
        appendRefreshLog(byIds && chunks.length > 1
            ? `낙찰 갱신 요청 전송 — 총 ${ids.length}건을 ${REFRESH_CHUNK}건씩 ${chunks.length}번에 나눠 처리`
            : "낙찰 갱신 요청 전송");

        for (let i = 0; i < chunks.length; i++) {
            if (chunks.length > 1) appendRefreshLog(`── ${i + 1}/${chunks.length}번째 묶음 시작`);
            // 한 청크가 실패해도 다음 묶음은 돌린다 — 앞 묶음의 결과는 이미 저장돼 있다.
            await runWinningStream(chunks[i], i * REFRESH_CHUNK, byIds ? ids.length : 0);
        }
    };

    /**
     * 공고 데이터 갱신 — SSE 진행률 스트리밍.
     *
     * <p>백엔드 {@code POST /api/bids/refresh-announcements-by-period/stream} 호출.
     * 입력 기간(최대 31일)의 입찰공고를 외부 API 로 재수집하여 신규 INSERT 또는 같은 (공고번호,차수) 메타 UPDATE.
     *
     * <p>SSE 이벤트:
     *  - started  : { startDate, endDate }
     *  - progress : { stage, message, fetched/total/createdCount/updatedCount }
     *  - done     : { otherFetched, workFetched, createdCount, updatedCount, totalSaved, elapsedMs }
     *  - error    : { code, resultCode, message, rawMessage }
     */
    const [isRefreshingAnn, setIsRefreshingAnn] = useState(false);
    const [annProgress, setAnnProgress] = useState(null);
    const [annError, setAnnError] = useState(null);
    /** 공고 갱신 SSE 단계별 메시지 누적. */
    const [annLog, setAnnLog] = useState([]);
    const appendAnnLog = (msg) => setAnnLog((prev) => [
        ...prev,
        { at: new Date(), text: msg },
    ]);
    const [annStartedAt, setAnnStartedAt] = useState(null);
    const annLogRef = useRef(null);
    useEffect(() => {
        if (annLogRef.current) {
            annLogRef.current.scrollTop = annLogRef.current.scrollHeight;
        }
    }, [annLog]);

    /** ETA — 진행률·경과시간 기반 남은시간 추정. processed=0 이면 null. */
    const formatEta = (startedAt, processed, total) => {
        if (!startedAt || !processed || !total || processed >= total) return null;
        const elapsed = Date.now() - startedAt;
        const remain = (elapsed / processed) * (total - processed);
        const sec = Math.round(remain / 1000);
        if (sec < 60) return `약 ${sec}초 남음`;
        const min = Math.floor(sec / 60);
        const s = sec % 60;
        return `약 ${min}분 ${s}초 남음`;
    };

    /**
     * 갱신 진행 중 — 모든 사용자 입력 차단의 단일 신호.
     * 검색/필터/키워드/페이지네이션 disabled + 라우트 이동 / 뒤로가기 confirm 에 사용.
     * 갱신 도중 사용자가 페이지를 떠나면 백엔드 작업이 IOException 으로 강제 중단되어 데이터 손실 위험.
     */
    const isBusy = isRefreshing || isRefreshingAnn;

    // 확인(yes/no) 모달만 이 훅에서 쓴다 — 컴포넌트 트리 끝에 <NotifyHost /> 마운트.
    // 단순 알림은 앱 전역 토스트(util/toast 의 notify)로 통일했다.
    const { confirmAsync, NotifyHost } = useNotifyDialog();

    const STAGE_LABEL = {
        // 일자 분할 수집 (현재)
        DAY_FETCHING: "수집 중",
        DAY_FETCHED: "수집 완료",
        DAY_SAVING: "DB 저장 중",
        DAY_DONE: "일자 완료",
        // 레거시(전체 일괄) 응답 호환 — 백엔드 롤백 시 표시 깨지지 않게 유지
        FETCHING_OTHER: "기타 공고 수집 중",
        FETCHING_OTHER_DONE: "기타 공고 수집 완료",
        FETCHING_WORK: "용역 공고 수집 중",
        FETCHING_WORK_DONE: "용역 공고 수집 완료",
        DEDUP: "중복 제거 중",
        DB_LOOKUP: "기존 데이터 조회 중",
        SAVING: "DB 저장 중",
    };

    const refreshAnnouncements = async () => {
        if (!validateRefreshPeriod()) return;
        const ok = await confirmAsync({
            title: "공고 데이터 갱신",
            message: `${startDate} ~ ${endDate} 기간 입찰공고를 외부 API 에서 다시 수집/갱신합니다. 하루를 4시간 단위 6개 청크로 분할 병렬 수집 후 일자별 취합 → 진행 상황은 실시간으로 표시됩니다 (한 달 기준 약 30초~1분). 계속할까요?`,
        });
        if (!ok) return;

        setIsRefreshingAnn(true);
        setAnnError(null);
        setAnnLog([]);
        setAnnStartedAt(Date.now());
        setAnnProgress({ stage: "starting", message: "준비 중..." });
        appendAnnLog("공고 갱신 요청 전송");

        const ctrl = new AbortController();
        annAbortRef.current = ctrl;
        const token = localStorage.getItem("auth_token");
        const query = new URLSearchParams();
        query.append("startDate", startDate);
        query.append("endDate", endDate);
        const url = `${process.env.REACT_APP_API_BASE_URL}/bids/refresh-announcements-by-period/stream?${query.toString()}`;

        try {
            const response = await fetch(url, {
                method: "POST",
                signal: ctrl.signal,
                headers: {
                    "Accept": "text/event-stream",
                    ...(token ? { "X-TOKEN": token } : {}),
                },
            });
            if (!response.ok) {
                let msg = "공고 갱신 요청 실패";
                try { const json = await response.json(); msg = json?.message || msg; } catch (_) {}
                throw new Error(msg);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            let currentEvent = "message";
            let finalDone = null;
            let finalError = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n")) >= 0) {
                    const line = buffer.slice(0, idx).replace(/\r$/, "");
                    buffer = buffer.slice(idx + 1);
                    if (line === "") { currentEvent = "message"; continue; }
                    if (line.startsWith("event:")) {
                        currentEvent = line.slice(6).trim();
                    } else if (line.startsWith("data:")) {
                        const data = line.slice(5).trim();
                        try {
                            const parsed = JSON.parse(data);
                            if (currentEvent === "started") {
                                setAnnProgress({
                                    stage: "started",
                                    message: parsed.totalDays
                                        ? `공고 수집 시작 (${parsed.totalDays}일 분할)`
                                        : "공고 수집 시작...",
                                    totalDays: parsed.totalDays,
                                });
                                appendAnnLog(
                                    parsed.totalDays
                                        ? `수집 시작 — ${parsed.startDate} ~ ${parsed.endDate} (${parsed.totalDays}일)`
                                        : "수집 시작"
                                );
                            } else if (currentEvent === "progress") {
                                if (parsed.stage === "DAY_FETCHING") {
                                    // 일자 시작 — 진행률 바 갱신만, 로그는 단계 전환에서.
                                    setAnnProgress((prev) => ({
                                        ...prev,
                                        stage: parsed.stage,
                                        message: `${parsed.date} 수집 중 (${parsed.dayIndex}/${parsed.totalDays})`,
                                        date: parsed.date,
                                        dayIndex: parsed.dayIndex,
                                        totalDays: parsed.totalDays,
                                    }));
                                } else if (parsed.stage === "DAY_FETCHED") {
                                    setAnnProgress((prev) => ({
                                        ...prev,
                                        stage: parsed.stage,
                                        message: parsed.message,
                                        date: parsed.date,
                                        dayIndex: parsed.dayIndex,
                                        totalDays: parsed.totalDays,
                                        dayOtherFetched: parsed.dayOtherFetched,
                                        dayWorkFetched: parsed.dayWorkFetched,
                                    }));
                                    appendAnnLog(
                                        `[${parsed.dayIndex}/${parsed.totalDays}] ${parsed.date} · 수집 ` +
                                        `${(parsed.dayOtherFetched ?? 0) + (parsed.dayWorkFetched ?? 0)}건 ` +
                                        `(기타 ${parsed.dayOtherFetched ?? 0} / 용역 ${parsed.dayWorkFetched ?? 0}, ` +
                                        `dedup ${parsed.dayDedup ?? 0}, ${parsed.fetchMs ?? 0}ms)`
                                    );
                                } else if (parsed.stage === "DAY_SAVING") {
                                    setAnnProgress((prev) => ({
                                        ...prev,
                                        stage: parsed.stage,
                                        message: parsed.message,
                                        date: parsed.date,
                                        dayIndex: parsed.dayIndex,
                                        totalDays: parsed.totalDays,
                                    }));
                                    appendAnnLog(`[${parsed.dayIndex}/${parsed.totalDays}] ${parsed.date} · DB 저장 중 (${parsed.dayDedup ?? 0}건)`);
                                } else if (parsed.stage === "DAY_DONE") {
                                    setAnnProgress((prev) => ({
                                        ...prev,
                                        stage: parsed.stage,
                                        message: parsed.message,
                                        date: parsed.date,
                                        dayIndex: parsed.dayIndex,
                                        totalDays: parsed.totalDays,
                                        // 일자별
                                        dayOtherFetched: parsed.dayOtherFetched,
                                        dayWorkFetched: parsed.dayWorkFetched,
                                        dayCreated: parsed.dayCreated,
                                        dayUpdated: parsed.dayUpdated,
                                        // ★ unchanged 를 빠뜨리면 안 된다 — 아래 로그 줄은 parsed 를 직접 읽어서
                                        //   «누적 유지 15,068» 로 맞게 찍히는데, 상단 칩은 이 state 를 읽는다.
                                        //   그래서 «로그엔 나오는데 칩만 0» 인 상태가 됐다(재수집은 대부분이 유지라
                                        //   가장 큰 숫자가 통째로 0 으로 보였다).
                                        dayUnchanged: parsed.dayUnchanged,
                                        // 누적
                                        totalOtherFetched: parsed.totalOtherFetched,
                                        totalWorkFetched: parsed.totalWorkFetched,
                                        totalCreated: parsed.totalCreated,
                                        totalUpdated: parsed.totalUpdated,
                                        totalUnchanged: parsed.totalUnchanged,
                                    }));
                                    appendAnnLog(
                                        `[${parsed.dayIndex}/${parsed.totalDays}] ${parsed.date} ✅ — ` +
                                        `신규 ${parsed.dayCreated ?? 0} · 변경 ${parsed.dayUpdated ?? 0} · 유지 ${parsed.dayUnchanged ?? 0} ` +
                                        `(저장 ${parsed.daySaveMs ?? 0}ms / 전체 ${parsed.dayElapsedMs ?? 0}ms) ` +
                                        `[누적 신규 ${parsed.totalCreated ?? 0} · 변경 ${parsed.totalUpdated ?? 0} · 유지 ${parsed.totalUnchanged ?? 0}]`
                                    );
                                } else {
                                    // 레거시 stage (백엔드 롤백 대비)
                                    const label = STAGE_LABEL[parsed.stage] || parsed.message || parsed.stage;
                                    setAnnProgress({
                                        stage: parsed.stage,
                                        message: label,
                                        fetched: parsed.fetched,
                                        total: parsed.total,
                                    });
                                    const suffix = parsed.fetched != null
                                        ? ` (${parsed.fetched}건)`
                                        : (parsed.total != null ? ` (총 ${parsed.total}건 대상)` : "");
                                    appendAnnLog(`${label}${suffix}`);
                                }
                            } else if (currentEvent === "dayFailed") {
                                appendAnnLog(
                                    `⚠️ [${parsed.dayIndex}/${parsed.totalDays}] ${parsed.date} 실패 ` +
                                    `(${parsed.code}): ${parsed.message || ""}`
                                );
                            } else if (currentEvent === "done") {
                                finalDone = parsed;
                                appendAnnLog(
                                    `완료 — 신규 ${parsed.createdCount ?? 0} · 변경 ${parsed.updatedCount ?? 0} · 유지 ${parsed.unchangedCount ?? 0} · ` +
                                    `총 ${parsed.totalSaved ?? 0}` +
                                    (parsed.failedDays?.length ? ` (실패 ${parsed.failedDays.length}일)` : "")
                                );
                            } else if (currentEvent === "error") {
                                finalError = parsed;
                                appendAnnLog(`❌ 오류: ${parsed.message || parsed.code}`);
                            }
                        } catch (_) {}
                    }
                }
            }

            if (finalError) {
                setAnnError(finalError);
                notify(
                    `공고 갱신 중단\n사유: ${finalError.message}\n(코드: ${finalError.resultCode ?? finalError.code})`,
                    "error",
                    { autoClose: 8000 },
                );
            } else if (finalDone) {
                const failedCount = finalDone.failedDays?.length ?? 0;
                const succeededDays = finalDone.succeededDays ?? finalDone.totalDays ?? 0;
                notify(
                    (failedCount > 0 ? `공고 갱신 완료 (일부 실패)\n` : `공고 갱신 완료\n`) +
                    `수집 (기타/용역): ${finalDone.otherFetched ?? 0} / ${finalDone.workFetched ?? 0}\n` +
                    `신규 등록: ${finalDone.createdCount ?? 0}건\n` +
                    `변경: ${finalDone.updatedCount ?? 0}건\n` +
                    `유지(내용 동일): ${finalDone.unchangedCount ?? 0}건\n` +
                    `총 저장: ${finalDone.totalSaved ?? 0}건` +
                    (finalDone.totalDays ? `\n일자: ${succeededDays}/${finalDone.totalDays} 성공` : "") +
                    (failedCount > 0 ? `\n실패 일자(${failedCount}일): ${finalDone.failedDays.join(", ")}` : "") +
                    (finalDone.elapsedMs ? `\n소요: ${(finalDone.elapsedMs / 1000).toFixed(1)}초` : ""),
                    failedCount > 0 ? "warning" : "success",
                    { autoClose: 8000 },
                );
                setAnnProgress((p) => ({ ...p, ...finalDone, stage: "done", message: "완료" }));
                await fetchData();
            }
        } catch (err) {
            if (err.name === "AbortError") {
                appendAnnLog("⚠️ 사용자 이탈/취소 — 작업 중단됨");
                return;
            }
            console.error("공고 갱신 실패", err);
            setAnnError({ code: "NETWORK_ERROR", message: err.message });
            appendAnnLog(`❌ 네트워크 오류: ${err.message || ""}`);
            notify(`공고 갱신 실패 — ${err.message || "네트워크 오류"}`, "error");
        } finally {
            setIsRefreshingAnn(false);
            annAbortRef.current = null;
            setTimeout(() => setAnnProgress(null), 4000);
        }
    };

    useEffect(() => {
        // 키워드 먼저 로드. finally 후에 keywordsLoaded=true 가 되면서 아래 effect 가 첫 자동 fetch 를 트리거.
        fetchUserKeyword().finally(() => setKeywordsLoaded(true));
        // 언마운트 시 진행 중인 SSE 요청 일괄 취소 — 백엔드는 다음 send() 시점에 IOException → 작업 중단.
        return () => {
            if (refreshAbortRef.current) refreshAbortRef.current.abort();
            if (annAbortRef.current) annAbortRef.current.abort();
            if (fetchAbortRef.current) fetchAbortRef.current.abort();
        };
        // eslint-disable-next-line
    }, []);

    /**
     * 필터/키워드 변경 시 자동 재조회 — 사용자가 select 를 바꾸거나 키워드 추가/삭제하면 즉시,
     * 금액 입력은 debounce 후. 잘못된 기간(예: 시작 > 종료) 일 때는 silent 로 skip — alert 안 뜸.
     * 페이지는 항상 0 으로 리셋 (필터가 바뀐 결과는 첫 페이지부터 보는 게 자연스러움).
     */
    useEffect(() => {
        if (!keywordsLoaded) return;
        setPage(0);
        fetchData({ page: 0, silent: true });
        // eslint-disable-next-line
    }, [
        keywordsLoaded,
        keywords,
        startDate,
        endDate,
        dateField,
        statusFilter,
        debouncedMinPrice,
        debouncedMaxPrice,
    ]);

    // 탭 닫기/새로고침/외부 이동(beforeunload) 시:
    // 1) 갱신 진행 중이면 브라우저 표준 경고창 트리거 (returnValue 설정 필요)
    // 2) 사용자가 그래도 떠나기로 하면 진행 중 SSE abort → 백엔드도 cancel
    useEffect(() => {
        const handler = (e) => {
            if (isBusy) {
                e.preventDefault();
                // Chrome 등 일부 브라우저는 returnValue 설정해야 confirm 표시
                e.returnValue = "갱신이 진행 중입니다. 페이지를 떠나면 작업이 중단됩니다.";
            }
            if (refreshAbortRef.current) refreshAbortRef.current.abort();
            if (annAbortRef.current) annAbortRef.current.abort();
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isBusy]);

    // React Router 내부 이동 차단 — useHistory().block 으로 navigation prompt 띄움.
    // isBusy 가 true 인 동안에만 등록, false 가 되면 unblock.
    useEffect(() => {
        if (!isBusy) return;
        const unblock = history.block(() =>
            "갱신이 진행 중입니다. 페이지를 떠나면 작업이 중단됩니다. 정말 이동하시겠습니까?"
        );
        return () => unblock();
    }, [isBusy, history]);

    // 클라이언트 정렬 제거 — 서버 sort 가 진실. 백엔드 응답을 그대로 표시.
    const sortedResults = results;

    /**
     * 같은 공고번호의 차수 다른 row 를 한 그룹으로 묶는다.
     * - primary: 최신 차수(차수 desc, 동률 시 게시일 desc) 한 건 → 목록에 대표로 노출
     * - others : 그 외 이전 차수들 → 펼침(드롭다운) 시 하위 행으로 노출
     * 서버 페이지 정렬 순서를 유지(그룹의 첫 등장 순서)하며, 현재 페이지 안에서만 묶는다.
     */
    const groupedResults = useMemo(() => {
        const byNo = new Map();
        for (const item of sortedResults) {
            const k = item.bidNoticeNumber ?? `__id_${item.id}`;
            if (!byNo.has(k)) byNo.set(k, []);
            byNo.get(k).push(item);
        }
        const groups = [];
        const seen = new Set();
        for (const item of sortedResults) {
            const k = item.bidNoticeNumber ?? `__id_${item.id}`;
            if (seen.has(k)) continue;
            seen.add(k);
            const rows = [...byNo.get(k)].sort((a, b) => {
                const oa = a.bidNoticeOrder || "";
                const ob = b.bidNoticeOrder || "";
                if (oa !== ob) return ob.localeCompare(oa);                       // 차수 desc → 최신 먼저
                return String(b.rgstDt || "").localeCompare(String(a.rgstDt || "")); // 동률 시 게시일 desc
            });
            groups.push({ key: k, primary: rows[0], others: rows.slice(1) });
        }
        return groups;
    }, [sortedResults]);

    const toggleGroup = (key) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // 결과(페이지/검색)가 바뀌면 펼침 상태 초기화 — 이전 페이지의 펼침이 남지 않도록.
    useEffect(() => { setExpandedGroups(new Set()); }, [results]);

    // results 갱신 시 그 페이지에 표시될 공고들의 즐겨찾기 상태 일괄 조회.
    useEffect(() => {
        const ids = (results || []).map(r => r?.id).filter(Boolean);
        if (ids.length === 0) { setBidFavSet(new Set()); return; }
        let cancelled = false;
        (async () => {
            try {
                const favIds = await favoriteApi.filterBidIds(ids);
                if (!cancelled) setBidFavSet(new Set(Array.isArray(favIds) ? favIds : []));
            } catch { /* 즐겨찾기 표시 실패는 silent — 별이 안 보일 뿐 검색 결과는 정상 */ }
        })();
        return () => { cancelled = true; };
    }, [results]);

    // ─── 표시 유틸 ──────────────────────────────────────────
    /** 입찰공고 분류 칩 — 전자/공동/재공고/국제/용역구분 등. */
    const getCategoryChips = (item) => {
        const chips = [];
        if (item.bidMethdNm && item.bidMethdNm.includes("전자")) {
            chips.push({ label: "전자", color: "#2563eb", bg: "#dbeafe" });
        }
        if (item.intrbidYn === "Y") {
            chips.push({ label: "국제", color: "#7c3aed", bg: "#ede9fe" });
        }
        if (item.reNtceYn === "Y") {
            chips.push({ label: "재공고", color: "#dc2626", bg: "#fee2e2" });
        }
        if (item.srvceDivNm) {
            chips.push({ label: item.srvceDivNm, color: "#475569", bg: "#f1f5f9" });
        } else if (item.cmmnSpldmdCorpRgnLmtYn === "Y") {
            chips.push({ label: "지역제한", color: "#ea580c", bg: "#ffedd5" });
        }
        return chips;
    };

    /** 입찰 현황 계산 — 입찰전 / 마감(D-N) / 마감완료 / 개찰완료 / 유찰. */
    const getBidStatus = (item) => {
    // 취소공고는 «개찰 결과가 아직 없는 것» 이 아니라 «생길 일이 없는 것» 이다.
    // 다른 판정보다 먼저 본다 — 취소공고에도 개찰일시가 그대로 남아 있어서, 순서를 뒤로 두면
    // «결과 확인필요» 로 표시되고 사람이 «갱신» 을 누르게 된다. (실제로 R26BK01681863-001 이
    // 그렇게 보였다 — 개찰 결과는 취소되기 전 차수인 -000 에 있다)
        if (item.ntceKindNm === "취소공고") {
            return { label: "취소공고", color: "#b45309", bg: "#fef3c7" };
        }
        const now = toDayjs(new Date());
        const clse = item.bidClseDt ? toDayjs(item.bidClseDt) : null;
        const openg = item.opengDt ? toDayjs(item.opengDt) : null;

        if (item.bidOpenResult) {
            // 유찰은 명확한 근거(개찰결과구분 "유찰")로 확정된 경우만.
            if (item.bidFailedConfirmed) {
                return { label: "유찰", color: "#b91c1c", bg: "#fee2e2" };
            }
            // 낙찰자 확인됨 → 개찰완료.
            if (item.winningCompanyName) {
                return { label: "개찰완료", color: "#16a34a", bg: "#dcfce7" };
            }
            // 여기서 «결과 확인필요» 로 뭉개면 안 되는 둘을 먼저 가른다.
            //   · bidOpenResultEmptyData — 조달청이 기한(개찰+14일)까지 결과를 안 줘서 «결과 없음» 으로 종결한 건.
            //                              더 볼 것이 없다. 그런데 «확인필요» 로 뜨면 사람이 갱신 버튼을 누른다.
            //   · rbidRsn — 재입찰 확정. 이 차수엔 낙찰자가 영영 안 생긴다(유찰과 달리 유찰 플래그는 안 선다).
            // 남는 «확인필요» 는 진짜로 재추적 중인 것뿐이다 — 투찰 목록은 있는데 낙찰자만 아직 없는 경우.
            if (item.rbidRsn) return { label: "재입찰", color: "#c2410c", bg: "#ffedd5" };
            if (item.bidOpenResultEmptyData) return { label: "결과 없음", color: "#475569", bg: "#e2e8f0" };
            // 개찰은 됐으나 낙찰자/유찰 근거 미확보 → 유찰로 단정하지 않음.
            return { label: "결과 확인필요", color: "#64748b", bg: "#f1f5f9" };
        }
        if (clse && now.isAfter(clse)) {
            if (openg && now.isBefore(openg)) {
                return { label: "개찰대기", color: "#7c3aed", bg: "#ede9fe" };
            }
            return { label: "마감", color: "#64748b", bg: "#f1f5f9" };
        }
        if (clse) {
            const daysLeft = clse.diff(now, "day");
            if (daysLeft <= 1) {
                const hoursLeft = clse.diff(now, "hour");
                if (hoursLeft <= 0) return { label: "마감임박", color: "#dc2626", bg: "#fee2e2" };
                return { label: `마감(D-${daysLeft === 0 ? hoursLeft + "h" : "1"})`, color: "#dc2626", bg: "#fee2e2" };
            }
            if (daysLeft <= 3) return { label: `마감(D-${daysLeft})`, color: "#ea580c", bg: "#ffedd5" };
            return { label: "입찰전", color: "#2563eb", bg: "#dbeafe" };
        }
        return { label: "입찰전", color: "#2563eb", bg: "#dbeafe" };
    };

    /** 추정가격 포맷팅. */
    const fmtPrice = (n) => {
        if (n == null) return "—";
        return Number(n).toLocaleString() + "원";
    };

    /**
     * 입찰마감 컬럼에 표시할 D-day 라벨 — 색상 포함.
     * 마감 전: "D-N", 당일: "D-day", 마감 후: "마감", 데이터 없음: null.
     */
    const computeDday = (clseIso) => {
        if (!clseIso) return null;
        const now = toDayjs(new Date());
        const clse = toDayjs(clseIso);
        if (!clse.isValid()) return null;
        if (now.isAfter(clse)) return { label: "마감", color: "#64748b" };
        const days = clse.startOf("day").diff(now.startOf("day"), "day");
        if (days === 0) return { label: "D-day", color: "#dc2626" };
        if (days <= 3) return { label: `D-${days}`, color: "#ea580c" };
        return { label: `D-${days}`, color: "#2563eb" };
    };

    const handleAddKeyword = () => {
        if (!newKeyword?.trim()) {
            notify("키워드를 입력해주세요", "warning");
            return;
        }
        const trimmed = newKeyword.trim();

        if (keywords.includes(trimmed)) {
            notify("이미 등록된 키워드입니다", "warning");
            return;
        }
        if (trimmed.includes(",")) {
            notify("키워드에 쉼표(,) 는 사용할 수 없습니다.", "warning");
            return;
        }

        const updated = [...keywords, trimmed];
        setKeywords(updated);
        setHighlighted(trimmed);
        setTimeout(() => setHighlighted(""), 1200);
        setNewKeyword("");
        saveKeywordsToServer(updated);
    };

    const handleRemoveKeyword = (kw) => {
        const updated = keywords.filter((k) => k !== kw);
        setKeywords(updated);
        saveKeywordsToServer(updated);
    };

    const handleResetKeywords = () => {
        setKeywords([]);
        saveKeywordsToServer([]);
    };

    /** 헤더 클릭 정렬 — 서버에 sortBy/sortDir 전달, 페이지 0 부터 재조회. */
    const handleSort = (key) => {
        let nextAsc;
        if (sortKey === key) {
            nextAsc = !sortAsc;
            setSortAsc(nextAsc);
        } else {
            nextAsc = false; // 새 키는 desc 디폴트 (최신/큰 값 위로)
            setSortKey(key);
            setSortAsc(nextAsc);
        }
        setPage(0);
        fetchData({ page: 0, sortBy: key, sortDir: nextAsc ? "asc" : "desc" });
    };

    const sortLabel = (key, label) => {
        if (sortKey !== key) return label;
        return `${label} ${sortAsc ? "▲" : "▼"}`;
    };

    /**
     * 공고 한 행 렌더 — 그룹 대표(primary) 와 펼친 하위 차수(child) 를 같은 함수로 그린다.
     * @param item 공고
     * @param opts.isChild    하위(이전 차수) 행이면 들여쓰기·음영 처리
     * @param opts.childCount 대표 행에 묶인 이전 차수 개수 (>0 이면 펼침 토글 노출)
     * @param opts.expanded   펼침 여부
     * @param opts.onToggle   펼침 토글 콜백
     */
    const renderBidRow = (item, { isChild = false, childCount = 0, expanded = false, onToggle = null } = {}) => {
        const chips = getCategoryChips(item);
        const status = getBidStatus(item);
        return (
            <TableRow
                key={item.id ?? `${item.bidNoticeNumber}-${item.bidNoticeOrder}`}
                hover
                onClick={() => { if (!isBusy) setSelected(item); }}
                sx={{
                    ...ds.components.tableRowHover,
                    ...(isChild ? { bgcolor: "#f8fafc" } : {}),
                }}
            >
                {/* 공고명 + 분류 + 공고번호 */}
                <TableCell>
                    <Box sx={isChild ? { pl: 2, borderLeft: "2px solid #cbd5e1", ml: 0.5 } : {}}>
                      <Stack direction="row" spacing={0.6} alignItems="flex-start">
                        {/*
                          별은 공고 «왼쪽 고정 칸» 이다 — 칩과 같은 줄에 섞어 두면 칩이 없는 행에서
                          별 혼자 한 줄을 통째로 먹고, 공고명이 그만큼 아래로 밀린다.
                          토글 시 부모 row onClick(모달)이 안 열리게 하는 건 FavoriteStar 내부에서 처리.
                        */}
                        <Box sx={{ flexShrink: 0, pt: "1px" }}>
                            {item.id != null && (
                                <FavoriteStar
                                    checked={bidFavSet.has(item.id)}
                                    onToggle={(next) => toggleBidFav(item.id, next)}
                                />
                            )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* 칩이 하나도 없으면 이 줄 자체를 그리지 않는다 — 빈 줄이 남으면 행 높이만 늘어난다. */}
                        {(isChild || chips.length > 0) && (
                            <Stack direction="row" spacing={0.5} sx={{ mb: 0.4, flexWrap: "wrap", gap: 0.3, alignItems: "center" }}>
                                {isChild && (
                                    <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", mr: 0.3 }}>↳ 이전 차수</Box>
                                )}
                                {chips.map((c, ci) => (
                                    <Chip key={ci} label={c.label} size="small"
                                        sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10, height: 18, borderRadius: 0 }} />
                                ))}
                            </Stack>
                        )}
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.35, color: "#0f172a" }}>
                            {item.bidNtceNm || item.title || "—"}
                        </Typography>
                        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.3, flexWrap: "wrap", gap: 0.3 }}>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>
                                {item.bidNoticeNumber}-{item.bidNoticeOrder}
                            </Typography>
                            {/* 같은 공고번호의 이전 차수 펼침 토글 — row onClick(모달) 막고 그룹만 토글. */}
                            {childCount > 0 && (
                                <Box
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
                                    sx={{
                                        display: "inline-flex", alignItems: "center", gap: 0.1,
                                        cursor: "pointer", userSelect: "none",
                                        px: 0.6, py: 0.05,
                                        bgcolor: expanded ? "#e0e7ff" : "#eef2ff",
                                        color: "#4338ca", fontSize: 10.5, fontWeight: 800,
                                        border: "1px solid #c7d2fe",
                                        "&:hover": { bgcolor: "#e0e7ff" },
                                    }}
                                >
                                    {expanded
                                        ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                                        : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
                                    이전 차수 {childCount}건
                                </Box>
                            )}
                        </Stack>
                        {/* 낙찰자 노란 강조 — 개찰완료 + winner 있는 경우 한 줄 */}
                        {item.winningCompanyName && (
                            <Box sx={{
                                mt: 0.4,
                                display: "inline-block",
                                px: 0.7, py: 0.15,
                                bgcolor: "#fef9c3",
                                color: "#854d0e",
                                fontSize: 11,
                                fontWeight: 800,
                                borderLeft: "3px solid #ca8a04",
                                letterSpacing: "0.01em",
                            }}>
                                낙찰자: {item.winningCompanyName}
                            </Box>
                        )}
                        </Box>
                      </Stack>
                    </Box>
                </TableCell>

                {/* 계약방법 + 추정가격 */}
                <TableCell>
                    <Typography sx={{ fontSize: 12, color: "#475569" }}>
                        {item.cntrctCnclsMthdNm || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700,
                        color: item.presmptPrce ? "#dc2626" : "#94a3b8", mt: 0.3 }}>
                        {fmtPrice(item.presmptPrce)}
                    </Typography>
                </TableCell>

                {/* 현황 */}
                <TableCell sx={{ textAlign: "center" }}>
                    <Chip label={status.label} size="small"
                        sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 11, borderRadius: 0 }} />
                </TableCell>

                {/* 공고기관 / 수요기관 */}
                <TableCell>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                        {item.ntceInsttNm || "—"}
                    </Typography>
                    {item.dminsttNm && item.dminsttNm !== item.ntceInsttNm && (
                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.2 }}>
                            / {item.dminsttNm}
                        </Typography>
                    )}
                </TableCell>

                {/* 게시일시 */}
                <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                    {formatDateTimeMinute(item.rgstDt || item.bidNtceDt, "-")}
                </TableCell>

                {/* 입찰마감 + D-day */}
                <TableCell sx={{ fontSize: 12, color: "#2563eb", fontWeight: 600 }}>
                    <Box>{formatDateTimeMinute(item.bidClseDt, "-")}</Box>
                    {(() => {
                        const dd = computeDday(item.bidClseDt);
                        return dd ? (
                            <Box sx={{
                                display: "inline-block",
                                mt: 0.3,
                                px: 0.8,
                                py: 0.1,
                                fontSize: 10.5,
                                fontWeight: 800,
                                color: "#fff",
                                bgcolor: dd.color,
                                borderRadius: 0,
                                letterSpacing: "0.02em",
                            }}>
                                {dd.label}
                            </Box>
                        ) : null;
                    })()}
                </TableCell>
            </TableRow>
        );
    };


    return (
        <Box sx={{ ...ds.components.external.page, py: 5 }}>
            <Container maxWidth="full">
                <Stack spacing={2.5}>

                    <Box>
                        <Typography sx={ds.components.external.heroTitle}>통합 나라장터 검색</Typography>
                    </Box>


                    <Paper sx={{ ...ds.components.external.card, p: { xs: 2.25, md: 3 }, mb: 3 }}>

                        {/* ✅ 토스처럼: “필터 = 표” */}
                        <FilterTable>
                            {/* 검색 */}
                            <FilterRow label="검색">
                                <UnifiedSearchBar />
                            </FilterRow>

                            {/* 조회기간 */}
                            <FilterRow label="조회기간">
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.25}
                                    alignItems={{ xs: "stretch", md: "center" }}
                                >
                                    <TextField
                                        type="date"
                                        value={startDate}
                                        inputProps={{ max: today }}
                                        onChange={(e) => handleStartDateChange(e.target.value)}
                                        disabled={isLoading || isBusy}
                                        sx={{ width: { xs: "100%", md: 180 } }}
                                    />
                                    <Typography sx={{ color: ds.colors.textMuted }}>→</Typography>
                                    <TextField
                                        type="date"
                                        value={endDate}
                                        inputProps={{ max: today }}
                                        onChange={(e) => handleEndDateChange(e.target.value)}
                                        disabled={isLoading || isBusy}
                                        sx={{ width: { xs: "100%", md: 180 } }}
                                    />

                                    {/* 상한이 풀린 계정에만 표시 — 안 보이면 일반 한도(조회 60일·갱신 30일)다. */}
                                    {refreshScope.unlimited && (
                                        <Typography sx={{ fontSize: 12, color: ds.colors.textMuted, whiteSpace: "nowrap" }}>
                                            관리자 — 기간 제한 없음
                                        </Typography>
                                    )}

                                    {/* 조회 버튼 제거 — 필터/키워드 변경 시 자동 재조회 effect 가 담당. */}
                                    {isLoading && (
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: ds.colors.textMuted, fontSize: 12 }}>
                                            <CircularProgress size={16} />
                                            <span>조회 중</span>
                                        </Stack>
                                    )}
                                    {canRefresh && (
                                        <Tooltip title={`지정 기간 입찰공고를 외부 API 로 다시 수집/갱신 (${refreshLimitText})`}>
                                            <span>
                                                <ActionButton
                                                    variant="refresh"
                                                    onClick={refreshAnnouncements}
                                                    disabled={isLoading || isRefreshing}
                                                    loading={isRefreshingAnn}
                                                    label="공고 갱신"
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {canRefresh && (
                                        <Tooltip title={`기간 내 미갱신 공고의 낙찰 결과를 외부 API 로 일괄 갱신 (${refreshLimitText})`}>
                                            <span>
                                                <ActionButton
                                                    variant="refresh"
                                                    onClick={() => refreshWinning()}
                                                    disabled={isLoading || isRefreshingAnn}
                                                    loading={isRefreshing}
                                                    label="낙찰 갱신"
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </FilterRow>

                            {/* 상태 / 기간 기준 / 금액 */}
                            <FilterRow label="필터">
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.25}
                                    alignItems={{ xs: "stretch", md: "center" }}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    <Box sx={{ minWidth: 160 }}>
                                        <ComboBox clearable={false} disabled={isBusy}
                                            options={[
                                                { value: "rgstDt", label: "기간: 게시일시" },
                                                { value: "bidNtceDt", label: "기간: 공고일시" },
                                                { value: "bidClseDt", label: "기간: 마감일시" },
                                                { value: "opengDt", label: "기간: 개찰일시" },
                                            ]}
                                            value={dateField}
                                            onChange={(v) => setDateField(v)} />
                                    </Box>

                                    <Box sx={{ minWidth: 150 }}>
                                        <ComboBox clearable={false} disabled={isBusy}
                                            options={[
                                                { value: "", label: "상태: 전체" },
                                                { value: "BEFORE", label: "입찰전" },
                                                { value: "CLOSED", label: "마감" },
                                                { value: "OPENED", label: "개찰완료" },
                                                { value: "UNSOLD", label: "유찰" },
                                            ]}
                                            value={statusFilter}
                                            onChange={(v) => setStatusFilter(v)} />
                                    </Box>

                                    <TextField
                                        size="small"
                                        type="number"
                                        placeholder="최소 금액(원)"
                                        value={minPrice}
                                        onChange={(e) => setMinPrice(e.target.value)}
                                        disabled={isBusy}
                                        sx={{ width: 150 }}
                                        inputProps={{ min: 0 }}
                                    />
                                    <Typography sx={{ color: ds.colors.textMuted }}>~</Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        placeholder="최대 금액(원)"
                                        value={maxPrice}
                                        onChange={(e) => setMaxPrice(e.target.value)}
                                        disabled={isBusy}
                                        sx={{ width: 150 }}
                                        inputProps={{ min: 0 }}
                                    />

                                    <ActionButton
                                        variant="secondary"
                                        startIcon={false}
                                        disabled={isBusy}
                                        onClick={() => {
                                            setStatusFilter("");
                                            setMinPrice("");
                                            setMaxPrice("");
                                            setDateField("rgstDt");
                                        }}
                                        sx={{ borderColor: "transparent",
                                            "&:hover": { borderColor: "transparent", bgcolor: ds.colors.primaryBg } }}
                                        label="필터 초기화"
                                    />
                                    {/* 명시적 조회 버튼 — 자동 fetch 외에 사용자 의도 즉시 재조회 */}
                                    <ActionButton
                                        variant="primary"
                                        startIcon={false}
                                        disabled={isLoading || isBusy}
                                        onClick={handleSearch}
                                        label="조회"
                                    />
                                </Stack>
                            </FilterRow>

                            {/* 키워드 */}
                            <FilterRow label="키워드" noDivider>
                                <Stack spacing={1.25}>
                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                        {keywords.map((k) => (
                                            <Chip
                                                key={k}
                                                label={k}
                                                onDelete={isBusy ? undefined : (() => handleRemoveKeyword(k))}
                                                deleteIcon={<CloseIcon />}
                                                disabled={isBusy}
                                                sx={{
                                                    ...ds.components.external.chipActive,
                                                    bgcolor: highlighted === k ? ds.colors.primaryLight : ds.colors.primaryBg,
                                                }}
                                            />
                                        ))}
                                    </Stack>

                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
                                        <TextField
                                            placeholder="키워드 추가"
                                            value={newKeyword}
                                            onChange={(e) => setNewKeyword(e.target.value)}
                                            disabled={isBusy}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleAddKeyword();
                                            }}
                                            sx={{ maxWidth: { xs: "100%", sm: 360 } }}
                                        />

                                        <ActionButton variant="add" onClick={handleAddKeyword} disabled={isBusy} label="추가" />
                                        <ActionButton variant="refresh" onClick={handleResetKeywords} disabled={isBusy} label="초기화" />
                                    </Stack>
                                </Stack>
                            </FilterRow>
                        </FilterTable>
                    </Paper>

                    {/* ── 공고 갱신 진행률 패널 (SSE) ── */}
                    {annProgress && (() => {
                        // 일자 분할 진행률 — dayIndex/totalDays 기반 determinate 바.
                        const hasDayProgress = annProgress.totalDays > 0 && annProgress.dayIndex != null;
                        const dayPercent = hasDayProgress
                            ? Math.min(100, Math.round((annProgress.dayIndex / annProgress.totalDays) * 100))
                            : 0;
                        const isDone = annProgress.stage === "done";
                        // 진행 중에도 라이브 누적 통계 (DAY_DONE 이벤트에서 채워짐).
                        // done 단계에서는 최종 finalDone 의 키(createdCount 등)를 우선.
                        const liveOther    = isDone ? (annProgress.otherFetched ?? 0)   : (annProgress.totalOtherFetched ?? 0);
                        const liveWork     = isDone ? (annProgress.workFetched ?? 0)    : (annProgress.totalWorkFetched ?? 0);
                        const liveCreated  = isDone ? (annProgress.createdCount ?? 0)   : (annProgress.totalCreated ?? 0);
                        const liveUpdated  = isDone ? (annProgress.updatedCount ?? 0)   : (annProgress.totalUpdated ?? 0);
                        // 유지 = 기존 row 인데 저장 대상 필드가 전부 동일 — «갱신» 으로 세면
                        // 실제로 뭐가 달라졌는지 알 수 없고 진짜 변경이 그 숫자에 묻힌다.
                        const liveUnchanged = isDone ? (annProgress.unchangedCount ?? 0) : (annProgress.totalUnchanged ?? 0);
                        const liveTotalSaved = isDone ? (annProgress.totalSaved ?? 0)   : (liveCreated + liveUpdated);
                        const showLive = hasDayProgress || isDone;
                        return (
                        <Paper sx={{ ...ds.components.external.card, p: { xs: 2, md: 2.5 }, mb: 3 }}>
                            <Stack spacing={1.2}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                        {annError ? "❌ 공고 갱신 중단" :
                                         isDone ? "✅ 공고 갱신 완료" :
                                         "공고 데이터 갱신 진행 중"}
                                    </Typography>
                                    {hasDayProgress && !isDone && (
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: ds.colors.textMuted }}>
                                            ({annProgress.dayIndex}/{annProgress.totalDays} · {dayPercent}%)
                                        </Typography>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography sx={{ fontSize: 12, color: ds.colors.textMuted }}>
                                        {annProgress.message || annProgress.stage}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant={(hasDayProgress || isDone) ? "determinate" : "indeterminate"}
                                    value={isDone ? 100 : dayPercent}
                                    color={annError ? "error" : "primary"}
                                    sx={{ height: 6, borderRadius: 0 }}
                                />
                                {annLog.length > 0 && (
                                    <Box ref={annLogRef} sx={{
                                        mt: 0.5,
                                        maxHeight: 180,
                                        overflowY: "auto",
                                        fontFamily: "monospace",
                                        fontSize: 11.5,
                                        color: ds.colors.textMuted,
                                        bgcolor: ds.colors.borderLight,
                                        p: 1,
                                    }}>
                                        {annLog.map((entry, i) => (
                                            <Box key={i} sx={{ lineHeight: 1.5 }}>
                                                <span style={{ opacity: 0.55, marginRight: 6 }}>
                                                    {entry.at.toLocaleTimeString()}
                                                </span>
                                                {entry.text}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {showLive && (
                                    <Stack direction="row" spacing={2} sx={{ fontSize: 12, color: ds.colors.textMuted, flexWrap: "wrap" }}>
                                        <span>수집(기타) <b>{liveOther}</b></span>
                                        <span>수집(용역) <b>{liveWork}</b></span>
                                        <span>신규 <b style={{ color: "#16a34a" }}>{liveCreated}</b></span>
                                        <span>변경 <b style={{ color: "#2563eb" }}>{liveUpdated}</b></span>
                                        <span>유지 <b style={{ color: ds.colors.textMuted }}>{liveUnchanged}</b></span>
                                        <span>총 <b>{liveTotalSaved}</b></span>
                                    </Stack>
                                )}
                                {annError && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        {annError.message}
                                        {annError.resultCode && (
                                            <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.5 }}>
                                                [code: {annError.resultCode}] {annError.rawMessage}
                                            </Typography>
                                        )}
                                    </Alert>
                                )}
                            </Stack>
                        </Paper>
                        );
                    })()}

                    {/* ── 낙찰 갱신 진행률 패널 (SSE) ── */}
                    {refreshProgress && (
                        <Paper sx={{ ...ds.components.external.card, p: { xs: 2, md: 2.5 }, mb: 3 }}>
                            <Stack spacing={1.2}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                        {refreshError ? "❌ 갱신 중단" :
                                         refreshProgress.phase === "starting" ? "준비 중..." :
                                         refreshProgress.processed === refreshProgress.total && refreshProgress.total > 0
                                            ? "✅ 완료" : "낙찰 갱신 진행 중"}
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <Typography sx={{ fontSize: 12, color: ds.colors.textMuted }}>
                                        {refreshProgress.processed} / {refreshProgress.total}
                                        {refreshProgress.total > 0 && ` (${refreshProgress.percent}%)`}
                                        {(() => {
                                            const eta = formatEta(refreshStartedAt, refreshProgress.processed, refreshProgress.total);
                                            return eta ? ` · ${eta}` : "";
                                        })()}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant={refreshProgress.total > 0 ? "determinate" : "indeterminate"}
                                    value={refreshProgress.percent || 0}
                                    color={refreshError ? "error" : "primary"}
                                    sx={{ height: 6, borderRadius: 0 }}
                                />
                                {refreshLog.length > 0 && (
                                    <Box ref={refreshLogRef} sx={{
                                        mt: 0.5,
                                        maxHeight: 180,
                                        overflowY: "auto",
                                        fontFamily: "monospace",
                                        fontSize: 11.5,
                                        color: ds.colors.textMuted,
                                        bgcolor: ds.colors.borderLight,
                                        p: 1,
                                    }}>
                                        {refreshLog.map((entry, i) => (
                                            <Box key={i} sx={{ lineHeight: 1.5 }}>
                                                <span style={{ opacity: 0.55, marginRight: 6 }}>
                                                    {entry.at.toLocaleTimeString()}
                                                </span>
                                                {entry.text}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                <Stack direction="row" spacing={2} sx={{ fontSize: 12, color: ds.colors.textMuted }}>
                                    <span>✓ 성공 <b style={{ color: "#16a34a" }}>{refreshProgress.succeeded}</b></span>
                                    <span>✗ 실패 <b style={{ color: "#dc2626" }}>{refreshProgress.failed}</b></span>
                                    {refreshProgress.currentBidNo && (
                                        <span>현재 공고: <code>{refreshProgress.currentBidNo}</code></span>
                                    )}
                                </Stack>
                                {refreshError && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                        {refreshError.message}
                                        {refreshError.resultCode && (
                                            <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.5 }}>
                                                [code: {refreshError.resultCode}] {refreshError.rawMessage}
                                            </Typography>
                                        )}
                                    </Alert>
                                )}
                            </Stack>
                        </Paper>
                    )}

                    <Paper sx={{ ...ds.components.external.card, p: { xs: 2.25, md: 3 }, mb: 3 }}>

                        {/* Tabs — 전체 / 즐겨찾는 공고 / 즐겨찾는 기업 */}
                        <Tabs value={tab} onChange={(_, v) => setTab(v)}
                              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
                            <Tab label="전체 공고" />
                            <Tab label={`즐겨찾는 공고${favBidItems.length > 0 ? ` (${favBidItems.length})` : ""}`} />
                            <Tab label={`즐겨찾는 기업${favCompanyItems.length > 0 ? ` (${favCompanyItems.length})` : ""}`} />
                            {/* 권한이 없으면 탭 자체를 그리지 않는다 — 눌러 보고 403 을 받는 것보다 낫다.
                                MUI Tabs 는 자식이 조건부로 사라져도 인덱스가 밀리지 않게 배열 끝에 둔다. */}
                            {canRefresh && <Tab label="개찰 추적 (관리자)" />}
                        </Tabs>

                        {/* ─── 탭 3: 개찰 추적 현황판 (관리자) ─── */}
                        {tab === TRACK_TAB && canRefresh && (
                            <Stack spacing={1.25}>
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                                    <Typography sx={{ fontWeight: 800 }}>
                                        개찰 추적{" "}
                                        <Box component="span" sx={{ color: ds.colors.textMuted }}>
                                            총 {(trackData?.totalElements ?? 0).toLocaleString()}건
                                        </Box>
                                    </Typography>
                                    <Box sx={{ width: 180 }}>
                                        <ComboBox
                                            options={TRACK_STATUS}
                                            value={trackStatus}
                                            onChange={(v) => { setTrackStatus(v || "ALL"); setTrackPage(0); }}
                                        />
                                    </Box>
                                    <ActionButton variant="refresh"
                                        onClick={() => setTrackReload((v) => v + 1)}
                                        disabled={trackLoading}
                                        label="새로고침" />
                                    {/*
                                      기간이 아니라 «지금 이 쪽에 보이는 것» 을 갱신한다.
                                      화면에 이미 대상이 떠 있는데 날짜로 환산해 부르면
                                      화면에 없는 건까지 딸려 온다 — 보이는 것과 도는 것이 같아야 한다.
                                      끝나면 목록을 다시 읽어 처리된 건이 빠지는 걸 눈으로 보게 한다.
                                    */}
                                    <ActionButton variant="add"
                                        onClick={async () => {
                                            const ids = (trackData?.content || [])
                                                .map((r) => r.id).filter((v) => v != null);
                                            if (!ids.length) return;
                                            await refreshWinning(ids);
                                            setTrackReload((v) => v + 1);
                                        }}
                                        disabled={trackLoading || isRefreshing || !trackData?.content?.length}
                                        label={`이 쪽 ${(trackData?.content?.length ?? 0).toLocaleString()}건 갱신`} />

                                    {/*
                                      화면에 그리지 않고 필터에 걸린 전체를 돌린다.
                                      5만 건을 갱신하려고 5만 행을 렌더할 이유가 없다 — id 만 받아
                                      500건씩 나눠 돌린다(표는 100건만 그려도 된다).
                                      건수가 크면 몇 시간짜리가 되므로, 누르기 전에 예상 시간을 말해 준다.
                                    */}
                                    <ActionButton variant="add"
                                        onClick={async () => {
                                            const total = trackData?.totalElements ?? 0;
                                            if (!total) return;
                                            let ids;
                                            try {
                                                ids = await api.get(
                                                    `${process.env.REACT_APP_API_BASE_URL}/bids/open-tracking/ids?status=${trackStatus}`);
                                            } catch (e) {
                                                notify("대상 목록을 가져오지 못했습니다.", "error");
                                                return;
                                            }
                                            if (!Array.isArray(ids) || !ids.length) return;

                                            // 실측 기준 초당 약 2건(2023년 건은 크롤링·유찰·재입찰까지 탄다).
                                            const mins = Math.ceil(ids.length / 2 / 60);
                                            const cut = total - ids.length;
                                            const ok = await confirmAsync({
                                                title: "필터 전체 갱신",
                                                message:
                                                    `${ids.length.toLocaleString()}건을 500건씩 ${Math.ceil(ids.length / 500)}번에 나눠 갱신합니다.\n` +
                                                    `예상 약 ${mins.toLocaleString()}분 — 그동안 이 창을 열어 두셔야 합니다.\n` +
                                                    (cut > 0 ? `\n※ 총 ${total.toLocaleString()}건 중 앞에서부터 ${ids.length.toLocaleString()}건만 처리합니다(한 번 상한).\n` : "") +
                                                    `\n중간에 끊겨도 처리된 건은 저장되며, 다시 눌러도 끝난 건은 건너뜁니다.`,
                                            });
                                            if (!ok) return;
                                            await refreshWinning(ids, true);
                                            setTrackReload((v) => v + 1);
                                        }}
                                        disabled={trackLoading || isRefreshing || !trackData?.totalElements}
                                        label={`필터 전체 ${(trackData?.totalElements ?? 0).toLocaleString()}건 갱신`} />
                                    {/*
                                      ★ 이 표는 «찍은 순간의 사진» 이다.
                                      스케줄러가 같은 정렬(개찰일시 DESC)로 사이클당 500건씩 처리하므로,
                                      목록 맨 위가 곧 스케줄러가 지금 씹고 있는 구간이다. 열어 둔 채로
                                      두면 이미 해결된 건을 «추적 중» 으로 보고 있게 된다 —
                                      실제로 목록의 «추적 중» 을 눌렀더니 상세는 «개찰완료» 인 일이 있었다.
                                    */}
                                    <Typography sx={{ fontSize: 11.5, color: ds.colors.textMuted }}>
                                        {trackFetchedAt
                                            ? `${trackFetchedAt.toLocaleTimeString()} 기준 · 한 사이클에 ${(refreshScope.openCycleCap || 2000).toLocaleString()}건씩 처리 (이전 사이클 종료 +10분)`
                                            : "결과가 아직 안 난 공고만 — 종결·취소공고는 담지 않습니다"}
                                    </Typography>
                                </Stack>

                                {trackLoading ? (
                                    <Box sx={{ py: 4, textAlign: "center" }}><LoadingDots /></Box>
                                ) : !trackData?.content?.length ? (
                                    <Box sx={{ py: 5, textAlign: "center", color: ds.colors.textMuted }}>
                                        <Typography sx={{ fontSize: 14 }}>해당 상태의 공고가 없습니다.</Typography>
                                    </Box>
                                ) : (
                                    <>
                                    <TableContainer sx={ds.components.tableContainer}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                <TableRow sx={ds.components.tableHead}>
                                                    <TableCell sx={{ width: 44 }}>No</TableCell>
                                                    <TableCell sx={{ minWidth: 300 }}>공고명 / 공고번호</TableCell>
                                                    <TableCell sx={{ width: 150 }}>개찰일시</TableCell>
                                                    <TableCell sx={{ width: 150 }}>다음 확인</TableCell>
                                                    <TableCell sx={{ width: 130 }}>상태</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {trackData.content.map((r, i) => {
                                                    const st = TRACK_BADGE[r.status] || TRACK_BADGE.TRACKING;
                                                    return (
                                                        <TableRow key={`${r.bidNoticeNumber}-${r.bidNoticeOrder}`} hover
                                                                  sx={{ ...ds.components.tableRowHover, cursor: "pointer" }}
                                                                  onClick={() => setSelected({
                                                                      bidNoticeNumber: r.bidNoticeNumber,
                                                                      bidNoticeOrder: r.bidNoticeOrder,
                                                                  })}>
                                                            <TableCell sx={{ color: ds.colors.textMuted }}>
                                                                {trackPage * trackSize + i + 1}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                                                    {r.bidNtceNm || "—"}
                                                                </Typography>
                                                                <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: ds.colors.textMuted }}>
                                                                    {r.bidNoticeNumber}-{r.bidNoticeOrder}
                                                                    {r.ntceInsttNm ? ` · ${r.ntceInsttNm}` : ""}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                                                {formatDateTimeMinute(r.opengDt, "-")}
                                                            </TableCell>
                                                            {/*
                                                              예약이 비어 있는 걸 «다음 사이클» 이라고 적었는데 그건 사실이 아니었다.
                                                              대상이 20만 건인데 한 사이클은 상한(openCycleCap)만큼만 집는다 —
                                                              2023년 건은 수십~수백 사이클 뒤에나 차례가 온다.
                                                              현황판과 스케줄러가 같은 정렬(개찰일시 DESC)을 쓰므로,
                                                              화면상의 순번이 곧 처리 순번이다. 그걸로 «몇 번째 · 몇 사이클 뒤» 를 센다.
                                                              시각으로 못 적는 이유는 fixedDelay 라서다 — «이전 작업이 끝난 뒤 10분»
                                                              이고 사이클 길이가 대상에 따라 달라져 «몇 시 몇 분» 이 고정되지 않는다.
                                                            */}
                                                            <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                                                {(() => {
                                                                    if (r.status === "FAILED") {
                                                                        return <Box component="span" sx={{ color: "#b91c1c" }}>자동 추적 제외</Box>;
                                                                    }
                                                                    if (r.nextCheckAt) return formatDateTimeMinute(r.nextCheckAt);
                                                                    if (r.status === "BEFORE") {
                                                                        return <Box component="span" sx={{ color: ds.colors.textMuted }}>개찰 후 대상</Box>;
                                                                    }
                                                                    // 순번 추정은 정렬이 스케줄러와 같을 때만 뜻이 있다.
                                                                    if (trackStatus !== "TRACKING" && trackStatus !== "ALL") {
                                                                        return <Box component="span" sx={{ color: ds.colors.textMuted }}>예약 없음</Box>;
                                                                    }
                                                                    const rank = trackPage * trackSize + i + 1;
                                                                    const cap = Math.max(1, refreshScope.openCycleCap || 2000);
                                                                    const cycles = Math.ceil(rank / cap);
                                                                    return (
                                                                        <Box component="span" sx={{ color: ds.colors.textMuted }}>
                                                                            대기 {rank.toLocaleString()}번째
                                                                            <Box component="span" sx={{ ml: 0.6, opacity: 0.8 }}>
                                                                                · 약 {cycles.toLocaleString()}사이클 뒤
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                })()}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box component="span" sx={{
                                                                    px: 0.8, py: 0.2, borderRadius: 0.5,
                                                                    fontSize: 11, fontWeight: 800,
                                                                    bgcolor: st.bg, color: st.color, whiteSpace: "nowrap",
                                                                }}>
                                                                    {st.label}
                                                                </Box>
                                                                {r.failCount > 0 && (
                                                                    <Typography component="span" sx={{ ml: 0.6, fontSize: 11, color: ds.colors.textMuted }}>
                                                                        실패 {r.failCount}/3
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </ResizableTable>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        count={trackData.totalElements ?? 0}
                                        page={trackPage}
                                        onPageChange={(_, p) => setTrackPage(p)}
                                        rowsPerPage={trackSize}
                                        onRowsPerPageChange={(e) => { setTrackSize(parseInt(e.target.value, 10)); setTrackPage(0); }}
                                        rowsPerPageOptions={[100, 200, 500, 700, 1000, 3000, 5000, 10000, 30000, 50000]}
                                        labelRowsPerPage="쪽당"
                                    />
                                    </>
                                )}
                            </Stack>
                        )}

                        {/* ─── 탭 1: 즐겨찾는 공고 ─── */}
                        {tab === 1 && (
                            <Stack spacing={1}>
                                <Typography sx={{ fontWeight: 800 }}>
                                    즐겨찾는 공고 <Box component="span" sx={{ color: ds.colors.textMuted }}>
                                        총 {favBidItems.length}건
                                    </Box>
                                </Typography>
                                {favTabLoading ? (
                                    <Box sx={{ py: 4, textAlign: "center" }}><LoadingDots /></Box>
                                ) : favBidItems.length === 0 ? (
                                    <Box sx={{ py: 5, textAlign: "center", color: ds.colors.textMuted }}>
                                        <Typography sx={{ fontSize: 14 }}>아직 즐겨찾기한 공고가 없습니다.</Typography>
                                        <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                                            전체 공고 탭에서 ⭐ 별 아이콘을 눌러 즐겨찾기 추가
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer sx={ds.components.tableContainer}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                <TableRow sx={ds.components.tableHead}>
                                                    <TableCell sx={{ minWidth: 320 }}>공고</TableCell>
                                                    <TableCell sx={{ minWidth: 200 }}>공고기관</TableCell>
                                                    <TableCell sx={{ width: 150 }}>입찰마감</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {favBidItems.map((item) => (
                                                    <TableRow key={item.id} hover
                                                              onClick={() => setSelected(item)}
                                                              sx={ds.components.tableRowHover}>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                <FavoriteStar checked={true}
                                                                    onToggle={async () => {
                                                                        await toggleBidFav(item.id, false);
                                                                        // 탭 안 list 에서도 즉시 제거
                                                                        setFavBidItems(prev => prev.filter(b => b.id !== item.id));
                                                                    }} />
                                                                <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
                                                                    {item.bidNtceNm || "—"}
                                                                </Typography>
                                                            </Stack>
                                                            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3, fontFamily: "monospace" }}>
                                                                {item.bidNoticeNumber}-{item.bidNoticeOrder}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12.5 }}>
                                                            {item.ntceInsttNm || "—"}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            {item.bidClseDt ? formatDateTimeMinute(item.bidClseDt) : "—"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </ResizableTable>
                                    </TableContainer>
                                )}
                            </Stack>
                        )}

                        {/* ─── 탭 2: 즐겨찾는 기업 ─── */}
                        {tab === 2 && (
                            <Stack spacing={1}>
                                <Typography sx={{ fontWeight: 800 }}>
                                    즐겨찾는 기업 <Box component="span" sx={{ color: ds.colors.textMuted }}>
                                        총 {favCompanyItems.length}건
                                    </Box>
                                </Typography>
                                {favTabLoading ? (
                                    <Box sx={{ py: 4, textAlign: "center" }}><LoadingDots /></Box>
                                ) : favCompanyItems.length === 0 ? (
                                    <Box sx={{ py: 5, textAlign: "center", color: ds.colors.textMuted }}>
                                        <Typography sx={{ fontSize: 14 }}>아직 즐겨찾기한 기업이 없습니다.</Typography>
                                        <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                                            기업 검색 페이지에서 ⭐ 별 아이콘을 눌러 즐겨찾기 추가
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer sx={ds.components.tableContainer}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                <TableRow sx={ds.components.tableHead}>
                                                    <TableCell sx={{ minWidth: 320 }}>기업명</TableCell>
                                                    <TableCell sx={{ width: 180 }}>사업자번호</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {favCompanyItems.map((c) => (
                                                    <TableRow key={c.businessNumber} hover
                                                              onClick={() => {
                                                                  // 기업 검색 페이지로 이동 — 사업자번호 query 로 자동 검색.
                                                                  history.push(`/search/companies/?bizNumber=${encodeURIComponent(c.businessNumber)}`);
                                                              }}
                                                              sx={ds.components.tableRowHover}>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                <FavoriteStar checked={true}
                                                                    onToggle={async () => {
                                                                        try {
                                                                            await favoriteApi.removeCompany(c.businessNumber);
                                                                            setFavCompanyItems(prev => prev.filter(x => x.businessNumber !== c.businessNumber));
                                                                        } catch {}
                                                                    }} />
                                                                <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
                                                                    {c.name || "—"}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                            {c.businessNumber}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </ResizableTable>
                                    </TableContainer>
                                )}
                            </Stack>
                        )}

                        {/* ─── 탭 0: 전체 공고 (기존 결과 테이블) ─── */}
                        {tab === 0 && (
                        <>
                        {/* ✅ 결과 테이블: 토스 느낌 (외부 카드 보더 X, 표 자체만 정돈) */}
                        <Stack spacing={1}>
                            <Typography sx={{ fontWeight: 800 }}>
                                공고 목록 <Box component="span" sx={{ color: ds.colors.textMuted }}>
                                    {totalElements > 0 ? `총 ${totalElements.toLocaleString()}건` : "총 0건"}
                                </Box>
                            </Typography>

                            <TableContainer sx={ds.components.tableContainer}>
                                <ResizableTable size="small">
                                    <TableHead>
                                        <TableRow sx={ds.components.tableHead}>
                                            <TableCell onClick={() => handleSort("bidNtceNm")} sx={{ cursor: "pointer", minWidth: 320 }}>
                                                {sortLabel("bidNtceNm", "공고 / 분류")}
                                            </TableCell>
                                            <TableCell sx={{ width: 170 }}>
                                                계약 방법 / 추정가
                                            </TableCell>
                                            <TableCell sx={{ width: 100, textAlign: "center" }}>
                                                현황
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 200 }}>
                                                공고기관 / 수요기관
                                            </TableCell>
                                            <TableCell
                                                onClick={() => handleSort("rgstDt")}
                                                sx={{ cursor: "pointer", width: 150 }}>
                                                {sortLabel("rgstDt", "게시일시")}
                                            </TableCell>
                                            <TableCell
                                                onClick={() => handleSort("bidClseDt")}
                                                sx={{ cursor: "pointer", width: 150 }}>
                                                {sortLabel("bidClseDt", "입찰마감")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody sx={ds.components.tableBody}>
                                        {isLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ py: 3 }}>
                                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: "text.secondary" }}>
                                                        <CircularProgress size={18} />
                                                        <span>공고를 가져오고 있습니다...</span>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                {groupedResults.map(({ key, primary, others }) => {
                                                    const expanded = expandedGroups.has(key);
                                                    return (
                                                        <React.Fragment key={key}>
                                                            {renderBidRow(primary, {
                                                                childCount: others.length,
                                                                expanded,
                                                                onToggle: () => toggleGroup(key),
                                                            })}
                                                            {expanded && others.map((child) =>
                                                                renderBidRow(child, { isChild: true })
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}

                                                {!isLoading && sortedResults.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ py: 5, color: "text.secondary", textAlign: "center" }}>
                                                            검색 결과가 없습니다.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        )}
                                    </TableBody>
                                </ResizableTable>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={totalElements}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={pageSize}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[20, 50, 100, 200]}
                                labelRowsPerPage="페이지당 행 수"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from.toLocaleString()}–${to.toLocaleString()} / ${count.toLocaleString()}`
                                }
                                // 로딩 중 페이지 전환 차단 — race condition 방지
                                backIconButtonProps={{ disabled: isLoading || isBusy || page === 0 }}
                                nextIconButtonProps={{ disabled: isLoading || isBusy || (page + 1) * pageSize >= totalElements }}
                                SelectProps={{ disabled: isLoading || isBusy }}
                                sx={{ borderTop: `1px solid ${ds.colors.borderLight}`, "& .MuiTablePagination-toolbar": { px: 0 } }}
                            />
                        </Stack>
                        </>
                        )}
                    </Paper>



                </Stack>
            </Container>

            <BidDetailModal
                bidNumber={selected?.bidNoticeNumber}
                bidNoticeOrder={selected?.bidNoticeOrder}
                isOpen={!!selected}
                onClose={() => setSelected(null)}
            />

            {/* 공용 알림/확인 모달 — alert/confirm 대체. 페이지당 1개 마운트. */}
            <NotifyHost />
        </Box>
    );
}
